import { useLayoutEffect, useRef, useState } from "react";
import { ChevronRight, Gavel } from "lucide-react";
import {
  ROUND_ORDER,
  normalizeRound,
  toEntries,
  toArray,
  nameOf,
  voteParticipants,
} from "@/lib/format";
import MatchVotesDialog from "@/components/MatchVotesDialog";

/**
 * Groups pairings by round. Supports several shapes of `torneo` doc:
 *  - torneo.emparejamientos = { Filtros: [[a,b],...], Octavos: ... }
 *  - torneo.emparejamientos = { "Grupo 1": [a,b,c], "Grupo 2": [...] } (group stage)
 *  - torneo.emparejamientos = [{ ronda, a, b, winner, scoreA, scoreB }]
 */
function groupByRound(torneo) {
  const result = {};
  ROUND_ORDER.forEach((r) => (result[r] = []));
  if (!torneo) return result;

  const emp = torneo.emparejamientos;
  if (!emp) return result;

  const currentRound = normalizeRound(torneo.ronda) || "Filtros";

  if (Array.isArray(emp)) {
    emp.forEach((m, i) => {
      const r = normalizeRound(m.ronda || m.round) || currentRound;
      if (result[r]) {
        result[r].push({
          id: m.id || `${r}-${i}`,
          a: m.a || m.mc1 || m.competitorA,
          b: m.b || m.mc2 || m.competitorB,
          scoreA: m.scoreA ?? m.puntosA,
          scoreB: m.scoreB ?? m.puntosB,
          winner: m.winner || m.ganador,
        });
      }
    });
  } else if (typeof emp === "object") {
    const entries = toEntries(emp);
    const isGroupStage = entries.every(([k]) => /grupo/i.test(String(k)));
    const isBatallaStage = entries.every(([k]) =>
      /^(batalla|match|pelea|duelo)/i.test(String(k))
    );
    const isRoundKeyed = entries.some(([k]) =>
      /filtro|octavo|cuarto|semi|final/i.test(String(k))
    );

    if (isBatallaStage || (!isGroupStage && !isRoundKeyed)) {
      // 1v1 pairings keyed by "Batalla N" (or similar) under current round
      const sortedEntries = [...entries].sort((a, b) => {
        const na = Number(String(a[0]).match(/\d+/)?.[0] || 0);
        const nb = Number(String(b[0]).match(/\d+/)?.[0] || 0);
        return na - nb;
      });
      sortedEntries.forEach(([key, pair], i) => {
        const arr = toArray(pair);
        const a = arr[0];
        const b = arr[1];
        result[currentRound].push({
          id: `${key}-${i}`,
          label: key,
          a: typeof a === "string" ? a : a?.nombre || a?.name,
          b: typeof b === "string" ? b : b?.nombre || b?.name,
          scoreA: arr[2],
          scoreB: arr[3],
          winner: arr[4],
        });
      });
    } else if (isGroupStage) {
      const sortedEntries = [...entries].sort((a, b) => {
        const na = Number(String(a[0]).match(/\d+/)?.[0] || 0);
        const nb = Number(String(b[0]).match(/\d+/)?.[0] || 0);
        return na - nb;
      });
      sortedEntries.forEach(([key, members], i) => {
        const names = toArray(members).map((v) =>
          typeof v === "string" ? v : v?.nombre || v?.name || JSON.stringify(v)
        );
        result[currentRound].push({
          id: `${key}-${i}`,
          group: key,
          participants: names,
        });
      });
    } else {
      entries.forEach(([key, pairs]) => {
        const r = normalizeRound(key);
        if (!result[r]) return;
        toArray(pairs).forEach((p, i) => {
          if (Array.isArray(p)) {
            result[r].push({
              id: `${r}-${i}`,
              a: p[0],
              b: p[1],
              scoreA: p[2],
              scoreB: p[3],
              winner: p[4],
            });
          } else if (p && typeof p === "object") {
            result[r].push({
              id: p.id || `${r}-${i}`,
              a: p.a || p.mc1 || p.competitorA,
              b: p.b || p.mc2 || p.competitorB,
              scoreA: p.scoreA ?? p.puntosA,
              scoreB: p.scoreB ?? p.puntosB,
              winner: p.winner || p.ganador,
            });
          }
        });
      });
    }
  }

  return result;
}

function Row({ name, score, state, position }) {
  const advanced = state === "advanced";
  const eliminated = state === "eliminated";
  return (
    <div
      data-testid={`row-${name}`}
      className={`flex items-center justify-between gap-3 border-l-[3px] px-3 py-2.5 ${
        advanced
          ? "border-l-[#FF5500] bg-[#FF5500]/10"
          : eliminated
          ? "border-l-transparent opacity-45"
          : "border-l-transparent"
      }`}
    >
      <span className="flex min-w-0 items-center gap-2">
        {position != null && (
          <span className="w-5 shrink-0 font-display text-xs tracking-wider text-[#94A3B8]">
            {String(position).padStart(2, "0")}
          </span>
        )}
        <span
          className={`truncate font-body text-sm font-medium ${
            eliminated ? "text-[#94A3B8] line-through decoration-[#475569]" : "text-white"
          }`}
        >
          {name || "— TBD —"}
        </span>
      </span>
      <span className="flex shrink-0 items-center gap-2">
        {score != null && (
          <span className={`font-display text-lg tracking-wider ${advanced ? "text-[#FF5500]" : "text-[#94A3B8]"}`}>
            {score}
          </span>
        )}
        {advanced && (
          <span className="flex items-center gap-1 bg-[#FF5500] px-1.5 py-0.5 font-ui text-[9px] font-bold uppercase tracking-[0.15em] text-white">
            Avanza <ChevronRight className="h-3 w-3" />
          </span>
        )}
      </span>
    </div>
  );
}

function MatchCard({ match, isLive, cardRef, advancers, decided, hasVotes, onClick }) {
  const names = match.participants || [match.a, match.b];
  const stateOf = (n) => {
    if (!decided || !n) return "pending";
    return advancers.has(n) ? "advanced" : "eliminated";
  };
  const label = match.label || match.group;
  return (
    <button
      ref={cardRef}
      type="button"
      onClick={onClick}
      data-testid={`match-${match.id}`}
      className={`group relative w-[280px] shrink-0 border bg-[#0F172A] text-left transition-[border-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:border-[#FF5500] hover:shadow-[0_0_0_1px_#FF5500,0_12px_30px_-12px_rgba(255,85,0,0.5)] ${
        isLive ? "border-[#FF5500] live-match" : "border-[#1E293B]"
      }`}
    >
      <div className="flex items-center justify-between border-b border-[#1E293B] px-3 py-1.5">
        <span className="font-ui text-[9px] font-bold uppercase tracking-[0.25em] text-[#94A3B8]">
          {label}
        </span>
        <span className="flex items-center gap-1 font-ui text-[9px] font-bold uppercase tracking-[0.15em] text-[#94A3B8] opacity-0 transition-opacity group-hover:opacity-100">
          <Gavel className="h-3 w-3" /> {hasVotes ? "Ver votos" : "Sin votos"}
        </span>
      </div>
      <div className="divide-y divide-[#1E293B]">
        {names.map((n, i) => (
          <Row
            key={i}
            name={n}
            score={match.participants ? undefined : i === 0 ? match.scoreA : match.scoreB}
            state={stateOf(n)}
            position={match.participants ? i + 1 : undefined}
          />
        ))}
      </div>
      {isLive && (
        <span className="absolute -top-2.5 left-3 inline-flex items-center gap-1 bg-[#FF5500] px-2 py-0.5 font-ui text-[9px] font-bold uppercase tracking-[0.2em] text-white">
          <span className="h-1 w-1 rounded-full bg-white live-pulse" />
          En vivo
        </span>
      )}
    </button>
  );
}

function mergeRounds(torneos) {
  const result = {};
  ROUND_ORDER.forEach((r) => (result[r] = []));
  (torneos || []).forEach((t) => {
    const g = groupByRound(t);
    ROUND_ORDER.forEach((r) => {
      if (g[r].length > 0 && result[r].length === 0) result[r] = g[r];
    });
  });
  return result;
}

// Names that progressed out of each round = roster of the following round
function buildAdvancers(docs, event) {
  const byRound = {};
  docs.forEach((t) => (byRound[normalizeRound(t.ronda) || "Filtros"] = t));
  const out = {};
  ROUND_ORDER.forEach((r, i) => {
    const next = ROUND_ORDER.slice(i + 1).find((n) => byRound[n]);
    if (next) {
      out[r] = { set: new Set(toArray(byRound[next].listaNombres).map(nameOf)), decided: true };
    } else if (r === "Final" && event?.winner) {
      out[r] = { set: new Set([nameOf(event.winner)]), decided: true };
    } else {
      out[r] = { set: new Set(), decided: false };
    }
  });
  return out;
}

function votesForMatch(votes, round, match) {
  const names = new Set((match.participants || [match.a, match.b]).filter(Boolean));
  return votes.filter((v) => {
    if (normalizeRound(v.ronda) !== round) return false;
    const ps = voteParticipants(v);
    return ps.length > 0 && ps.every((p) => names.has(p));
  });
}

export default function Bracket({ torneo, torneos, votes = [], event }) {
  const docs = torneos && torneos.length > 0 ? torneos : torneo ? [torneo] : [];
  const grouped = mergeRounds(docs);
  const advancers = buildAdvancers(docs, event);
  const currentRound = normalizeRound(torneo?.ronda);
  const activeRounds = ROUND_ORDER.filter((r) => grouped[r].length > 0);
  const [selected, setSelected] = useState(null);

  const containerRef = useRef(null);
  const matchRefs = useRef({});
  const [connectors, setConnectors] = useState([]);
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });

  useLayoutEffect(() => {
    const compute = () => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      setContainerSize({ w: container.scrollWidth, h: container.scrollHeight });

      const lines = [];
      for (let i = 0; i < activeRounds.length - 1; i++) {
        const fromRound = activeRounds[i];
        const toRound = activeRounds[i + 1];
        const fromRefs = matchRefs.current[fromRound] || [];
        const toRefs = matchRefs.current[toRound] || [];
        const pairSize = fromRefs.length / Math.max(toRefs.length, 1);

        toRefs.forEach((toEl, j) => {
          if (!toEl) return;
          const fromStart = Math.floor(j * pairSize);
          const fromEnd = Math.floor((j + 1) * pairSize);
          for (let k = fromStart; k < fromEnd && k < fromRefs.length; k++) {
            const fromEl = fromRefs[k];
            if (!fromEl) continue;
            const a = fromEl.getBoundingClientRect();
            const b = toEl.getBoundingClientRect();
            const x1 = a.right - rect.left + container.scrollLeft;
            const y1 = a.top - rect.top + a.height / 2 + container.scrollTop;
            const x2 = b.left - rect.left + container.scrollLeft;
            const y2 = b.top - rect.top + b.height / 2 + container.scrollTop;
            const midX = (x1 + x2) / 2;
            lines.push({
              key: `${fromRound}-${k}->${toRound}-${j}`,
              d: `M${x1},${y1} L${midX},${y1} L${midX},${y2} L${x2},${y2}`,
            });
          }
        });
      }
      setConnectors(lines);
    };

    compute();
    const ro = new ResizeObserver(compute);
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener("resize", compute);
    const t = setTimeout(compute, 300);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", compute);
      clearTimeout(t);
    };
  }, [torneo, activeRounds.join("|")]);

  if (activeRounds.length === 0) {
    return (
      <div data-testid="bracket-empty" className="border border-[#1E293B] bg-[#0F172A] p-10 text-center">
        <p className="font-ui text-xs font-bold uppercase tracking-[0.25em] text-[#94A3B8]">
          Sin emparejamientos disponibles aún
        </p>
      </div>
    );
  }

  return (
    <div data-testid="bracket" className="relative border border-[#1E293B] bg-[#080D1A]">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-[#1E293B] px-6 py-3 font-ui text-[10px] font-bold uppercase tracking-[0.2em] text-[#94A3B8]">
        <span className="flex items-center gap-2"><span className="h-3 w-1 bg-[#FF5500]" /> Avanza a la siguiente ronda</span>
        <span className="flex items-center gap-2"><span className="h-3 w-5 line-through opacity-50">MC</span> Eliminado</span>
        <span className="flex items-center gap-2"><Gavel className="h-3 w-3" /> Pulsa un enfrentamiento para ver los votos</span>
      </div>
      <div className="overflow-x-auto">
        <div ref={containerRef} className="relative flex min-w-max items-stretch gap-14 p-6">
          <svg
            data-testid="bracket-connectors"
            className="pointer-events-none absolute inset-0"
            style={{ width: containerSize.w || "100%", height: containerSize.h || "100%" }}
            aria-hidden="true"
          >
            {connectors.map((c) => (
              <path key={c.key} d={c.d} fill="none" stroke="#334155" strokeWidth="1.5" />
            ))}
          </svg>

          {activeRounds.map((round, colIdx) => {
            const isCurrent = round === currentRound;
            const { set: adv, decided } = advancers[round];
            matchRefs.current[round] = [];
            return (
              <div
                key={round}
                className="relative z-10 flex min-w-[280px] flex-col fade-in-up"
                style={{ animationDelay: `${colIdx * 90}ms` }}
              >
                <div className="mb-6 flex items-end justify-between border-b-2 pb-3" style={{ borderColor: isCurrent ? "#FF5500" : "#1E293B" }}>
                  <div className="flex flex-col">
                    <span className="font-ui text-[9px] font-bold uppercase tracking-[0.3em] text-[#94A3B8]">
                      Ronda {colIdx + 1}
                    </span>
                    <h4 className={`font-display text-2xl tracking-wider ${isCurrent ? "text-[#FF5500]" : "text-white"}`}>
                      {round}
                    </h4>
                  </div>
                  <div className="flex flex-col items-end font-ui text-[10px] font-bold uppercase tracking-[0.2em] text-[#94A3B8]">
                    <span>{grouped[round].length} {grouped[round].length === 1 ? "enfrentamiento" : "enfrentamientos"}</span>
                    {decided && adv.size > 0 && <span className="text-[#FF5500]">{adv.size} avanzan</span>}
                  </div>
                </div>
                <div className="flex flex-1 flex-col justify-around gap-6">
                  {grouped[round].map((m, mi) => {
                    const mv = votesForMatch(votes, round, m);
                    return (
                      <MatchCard
                        key={m.id}
                        match={m}
                        isLive={isCurrent && !decided}
                        advancers={adv}
                        decided={decided}
                        hasVotes={mv.length > 0}
                        onClick={() => setSelected({ match: m, round, votes: mv })}
                        cardRef={(el) => {
                          if (!matchRefs.current[round]) matchRefs.current[round] = [];
                          matchRefs.current[round][mi] = el;
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <MatchVotesDialog
        match={selected?.match}
        round={selected?.round}
        votes={selected?.votes || []}
        advancers={selected ? advancers[selected.round].set : new Set()}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}
